import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { getCategories, getStoreInfo } from '@/api/item';
import { getSupportLanguages } from '@/api/customer';
import Constants from '@/constants/const';
import useHomepage, { HomepageDrawerCompatibleProvider } from '@/store/home';
import { useNavigationService } from '@/store/navigation';
import { CategoryType, CuisineCategory } from '@/types/category.d';
import { IStoreInfo } from '@/types/homepage';
import { isLatinoChannel } from '@/utils/common/latino-app';
import { getZipcode } from '@/utils/common/porder';
import { getValueFromCookie } from '@/utils/cookie';
import eventemitter, { actions } from '@/utils/eventemitter';

import usePageMountEffect from '../usePageMountEffect';

const GlobalNavHeaderContext = React.createContext(null);

type PageProps = {
  [key: string]: any;
};

type blockedCategories = {
  [key: string]: boolean;
};

type StoreInfoType = IStoreInfo & {
  [key: string]: any;
};

type HeaderCategory = CuisineCategory & CategoryType;

const globalNavBlockedCategories: blockedCategories = {
  global: true,
  new: true,
  trending: true,
  sale: true,
  freshgourmet: true,
  freshbakery: true
};

export const GlobalNavHeaderProvider = props => {
  const { homeData, userInfo } = props;

  const __extractCategories = (preComputedList?: HeaderCategory[], pageProps?: PageProps) => {
    let list: HeaderCategory[];
    if (preComputedList) {
      list = preComputedList;
    } else if (pageProps?.categories?.length && pageProps?.ssrProvidedGlobalNavCategories) {
      // Category page pattern
      list = pageProps.categories;
    }

    if (!list) return null;

    return list.filter((listItem: HeaderCategory) => {
      if (listItem?.key) {
        return !globalNavBlockedCategories[listItem?.key];
      }
      return false;
    });
  };

  const __extractStoreInfoFromCookie = () => {
    const fromCookie = getValueFromCookie(Constants.STORE_INFO);
    const weeeStoreFromCookie = getValueFromCookie(Constants.WEEE_STORE);
    const parsed = fromCookie && JSON.parse(fromCookie);
    if (parsed?.in_test && parsed?.select_store_name && parsed?.select_store == weeeStoreFromCookie) {
      return parsed;
    }
    return undefined;
  };

  const streamlinedHeader: boolean = !!props?.httpReqAwareProps?.streamlinedHeader ? true : false;
  const hasGlobalSupport: boolean = !!props?.httpReqAwareProps?.hasGlobalSupport ? true : false;
  const { t, lang } = useTranslation('common');
  const router = useRouter();
  const useDatagrailConsent = Boolean(props?.httpReqAwareProps?.useDatagrailConsent);
  const { store, dispatch } = useHomepage();
  const categories = useMemo(() => {
    return __extractCategories(undefined, props || {});
  }, [props]);
  const isLatino = isLatinoChannel();
  const langMayHaveStore = useMemo(() => {
    if (!isLatino) {
      return lang === Constants.LANGUAGE_EN;
    } else {
      return lang === Constants.LANGUAGE_EN || lang === Constants.LANGUAGE_ES;
    }
  }, [isLatino, lang]);

  const skipFallbackFetch = props?.blockDefaultGlobalNavCategories || false;
  const __storeInfo = useMemo(() => {
    let initState;
    if (props?.storeInfo) {
      // Right now just home page explicitily defines as server prop
      initState = props?.storeInfo;
    } else if (langMayHaveStore) {
      const storeInfoFromCookie = __extractStoreInfoFromCookie();
      if (storeInfoFromCookie) {
        initState = storeInfoFromCookie;
      }
    } else if (!langMayHaveStore) {
      initState = {
        in_test: false
      };
    }
    return initState;
  }, [store?.storeInfo, lang, props?.storeInfo, props?.skipCustomStoreDataFetch]);

  const __fetchCategoriesIfNeeded = () => {
    if (!categoryLinks && !skipFallbackFetch) {
      fetchCategoriesFallback();
    } else if (categoryLinks?.length && router?.asPath?.startsWith('/product/')) {
      const firstStandardCategy = categoryLinks.find(el => el.special_category === 0);
      if (!firstStandardCategy?.sub_categories?.length) {
        // Page has main categories from its SSR source or previous page state in client.
        // However, the subcategories are not populated
        fetchCategoriesFallback();
      } else {
        setCategoryLinksLoading(false);
      }
    } else {
      setCategoryLinksLoading(false);
    }
  };

  const __fetchStoreInfoAsyncIfNeeded = () => {
    if (props?.skipCustomStoreDataFetch) return null;
    const validStore = storeInfo && storeInfo?.select_store_id && storeInfo?.in_test !== false;
    if (!validStore && langMayHaveStore) {
      fetchStoreInfoAsync();
    }
  };

  const [categoryLinks, setCategoryLinks] = useState<HeaderCategory[]>(categories);
  const [categoryLinksLoading, setCategoryLinksLoading] = useState(true);
  const [asyncLoadedProduct, setAsyncLoadedProduct] = useState<ProductType>(props?.detail?.product);
  const [storeInfo, setStoreInfo] = useState<StoreInfoType>(__storeInfo);
  const [supportLanguages, setSupportLanguages] = useState<any[]>([]);
  const isFetchingSupportLanguages = useRef<boolean>(false);
  let routeNeedsNewCategoryFetch = false;

  const fetchSupportLanguages = async () => {
    // 关键优化：如果已经有数据，或者正在请求中，直接返回
    if (supportLanguages.length > 0 || isFetchingSupportLanguages.current) return;
    try {
      isFetchingSupportLanguages.current = true;
      const { object = {} } = await getSupportLanguages();
      const languages = (object?.language || []).map(item => {
        if (item.key === Constants.LANGUAGE_CHINESE_T_ALIAS) {
          return {
            ...item,
            key: Constants.LANGUAGE_CHINESE_T
          };
        }
        return item;
      });
      setSupportLanguages(languages);
    } catch (error) {
      console.error('Failed to fetch support languages in Provider', error);
    } finally {
      isFetchingSupportLanguages.current = false;
    }
  };

  const fetchCategoriesFallback = async () => {
    const zipcode = getZipcode();
    if (!zipcode) return;

    const response = await getCategories({
      from_page: 'home'
    });
    const categories = response?.object?.category_list;
    if (categories?.length) {
      const newList = __extractCategories(categories, undefined);
      setCategoryLinks(newList);
    }
    setCategoryLinksLoading(false);
  };

  const fetchStoreInfoAsync = async () => {
    const storeInfoFromCookie = __extractStoreInfoFromCookie();
    if (storeInfoFromCookie) {
      // Prevents wastefull API call
      setStoreInfo(storeInfoFromCookie);
    }

    const zipcode = getZipcode();
    if (!zipcode) return;

    const storeInfoResult = await getStoreInfo({ zipcode });
    if (storeInfoResult?.result) {
      const storeInfo = storeInfoResult?.object || {};
      setStoreInfo(storeInfo);
    }
  };

  useEffect(() => {
    const handleAsyncCategories = (data: any) => {
      if (data?.length > 0) {
        const newList = __extractCategories(data, undefined);
        setCategoryLinks(newList);
      }
    };
    eventemitter.on(actions.loadedGlobalNavHeaderCompatibleLinks, handleAsyncCategories);
    return () => {
      eventemitter.off(actions.loadedGlobalNavHeaderCompatibleLinks, handleAsyncCategories);
    };
  }, [categoryLinks]);

  useEffect(() => {
    setAsyncLoadedProduct(props?.detail?.product);
  }, [props?.detail?.product]);

  usePageMountEffect(() => {
    __fetchCategoriesIfNeeded();
    __fetchStoreInfoAsyncIfNeeded();
    fetchSupportLanguages();
  }, [router?.asPath]);

  const headerNavSizesConfig = useMemo(() => {
    let headerNavHeight = 116;
    let isPromoBannerSticky = false;

    const asPath = router?.asPath;
    if (asPath) {
      if (asPath.startsWith('/product/')) {
        isPromoBannerSticky = true;
      } else if (asPath.startsWith('/cart')) {
        isPromoBannerSticky = true;
      } else if (asPath.startsWith('/category/') || asPath.startsWith('/mkpl/category/')) {
        headerNavHeight = 86;
      }
    }

    return {
      headerNavHeight,
      isPromoBannerSticky
    };
  }, [router?.asPath]);

  const productParentCategory = useMemo(() => {
    const isProductRoute = router?.asPath?.startsWith('/product/');
    const product = !isProductRoute ? undefined : asyncLoadedProduct || props?.detail?.product;
    if (product) {
      const apiDefinedCategory = product.parent_category;

      let finalCategory = apiDefinedCategory;

      if (!finalCategory && router?.query?.category) {
        const subCategory = router.query.category as string; // example: &category=frozen10
        const mainCategory = (subCategory || '').replace(/\d+/g, '');
        if (mainCategory && subCategory?.startsWith(mainCategory)) {
          finalCategory = mainCategory;
        }
      }
      return finalCategory;
    } else if (props?.defaultMainCategory) {
      const subCategory = props.defaultMainCategory as string;
      const mainCategory = (subCategory || '').replace(/\d+/g, ''); // Be aware that sometimes we will have subcategory filters thete (/en/category/canned02/Packaged-Food)
      return mainCategory || subCategory;
    }
    return null;
  }, [
    router?.asPath,
    props?.detail?.product,
    router?.query?.category,
    props?.defaultMainCategory,
    asyncLoadedProduct?.category
  ]);

  const productSubCategory = useMemo(() => {
    const isProductRoute = router?.asPath?.startsWith('/product/');
    const product = !isProductRoute ? undefined : asyncLoadedProduct || props?.detail?.product;
    if (product) {
      const apiDefinedSubCategory = product.category;
      let finalSubCategory = apiDefinedSubCategory;

      if (apiDefinedSubCategory && router?.query?.category) {
        const urlMainCategory = ((router.query.category as string) || '').replace(/\d+/g, '');
        if (apiDefinedSubCategory?.startsWith(urlMainCategory)) {
          finalSubCategory = undefined; // Let url overwrite - likely navigation from category sub-filter
        }
      }
      if (!finalSubCategory && router?.query?.category) {
        const subCategory = router.query.category as string;
        const mainCategory = (subCategory || '').replace(/\d+/g, '');
        if (mainCategory && subCategory?.startsWith(mainCategory) && productParentCategory) {
          finalSubCategory = subCategory;
        }
      }
      return finalSubCategory;
    }
    return null;
  }, [
    router?.asPath,
    props?.detail?.product,
    router?.query?.category,
    productParentCategory,
    asyncLoadedProduct?.category
  ]);

  return (
    <HomepageDrawerCompatibleProvider data={{ storeInfo, homeData, userInfo }}>
      <GlobalNavHeaderContext.Provider
        value={{
          categoryLinks,
          categoryLinksLoading,
          setCategoryLinks,
          setAsyncLoadedProduct,
          storeInfo,
          streamlinedHeader,
          hasGlobalSupport,
          useDatagrailConsent,
          productParentCategory,
          productSubCategory,
          headerNavSizesConfig,
          supportLanguages
        }}>
        {props.children}
      </GlobalNavHeaderContext.Provider>
    </HomepageDrawerCompatibleProvider>
  );
};

type UseGlobalNavHeaderType = {
  categoryLinks: CuisineCategory[];
  categoryLinksLoading: boolean;
  setCategoryLinks: (categoryLinks: CuisineCategory[]) => void;
  setAsyncLoadedProduct?: (asyncProduct: ProductType) => void;
  streamlinedHeader?: boolean;
  storeInfo?: IStoreInfo;
  hasGlobalSupport: boolean;
  useDatagrailConsent?: boolean;
  productParentCategory?: string;
  productSubCategory?: string;
  headerNavSizesConfig: {};
  supportLanguages: any[];
};

export default function useGlobalNavHeader(): UseGlobalNavHeaderType {
  return React.useContext(GlobalNavHeaderContext);
}
